import type { Hip3Market } from "@/types/hip3";

const NEUTRAL_SUBSCORE = 0.5;
const FLOW_CAP = 0.85;

const WEIGHTS = {
  liquidity: 0.4,
  cost: 0.3,
  flow: 0.2,
  carry: 0.1,
} as const;

const ACTIVE_PENALTY = {
  active: 1,
  inactive: 0,
} as const;

type PreparedMarket = {
  market: Hip3Market;
  oiUsd?: number;
  turnover?: number;
  fundAbs?: number;
  spreadBps?: number;
  logOiUsd?: number;
  logTurnover?: number;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const percentile = (sortedValues: number[], percentileRank: number) => {
  if (!sortedValues.length) {
    return undefined;
  }
  if (sortedValues.length === 1) {
    return sortedValues[0];
  }

  const position = clamp(percentileRank, 0, 1) * (sortedValues.length - 1);
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);
  const weight = position - lowerIndex;

  const lowerValue = sortedValues[lowerIndex];
  const upperValue = sortedValues[upperIndex];

  if (!isFiniteNumber(lowerValue) || !isFiniteNumber(upperValue)) {
    return undefined;
  }

  return lowerValue + (upperValue - lowerValue) * weight;
};

const createNormalizer = (values: Array<number | undefined>) => {
  const sorted = values.filter(isFiniteNumber).sort((a, b) => a - b);
  const p10 = percentile(sorted, 0.1);
  const p90 = percentile(sorted, 0.9);

  if (
    !isFiniteNumber(p10) ||
    !isFiniteNumber(p90) ||
    p90 <= p10
  ) {
    return (_value?: number) => NEUTRAL_SUBSCORE;
  }

  return (value?: number) => {
    if (!isFiniteNumber(value)) {
      return NEUTRAL_SUBSCORE;
    }
    return clamp((value - p10) / (p90 - p10), 0, 1);
  };
};

const prepareMarket = (market: Hip3Market): PreparedMarket => {
  const oi = isFiniteNumber(market.openInterest) ? market.openInterest : undefined;
  const price = isFiniteNumber(market.currentPrice) ? market.currentPrice : undefined;
  const volume = isFiniteNumber(market.volume24h) ? market.volume24h : undefined;
  const annualFunding = isFiniteNumber(market.annualFunding) ? market.annualFunding : undefined;
  const spreadBps = isFiniteNumber(market.spreadBps) ? market.spreadBps : undefined;

  const oiUsd = oi !== undefined && price !== undefined ? oi * price : undefined;
  const turnover = oiUsd !== undefined && volume !== undefined ? volume / Math.max(oiUsd, 1) : undefined;
  const fundAbs = annualFunding !== undefined ? Math.abs(annualFunding) : undefined;

  return {
    market,
    oiUsd,
    turnover,
    fundAbs,
    spreadBps,
    logOiUsd: oiUsd !== undefined ? Math.log1p(oiUsd) : undefined,
    logTurnover: turnover !== undefined ? Math.log1p(turnover) : undefined,
  };
};

export function scoreMarkets(markets: Hip3Market[]): Hip3Market[] {
  if (!markets.length) {
    return [];
  }

  const prepared = markets.map(prepareMarket);

  const normalizeLiquidity = createNormalizer(prepared.map((market) => market.logOiUsd));
  const normalizeSpread = createNormalizer(prepared.map((market) => market.spreadBps));
  const normalizeCarry = createNormalizer(prepared.map((market) => market.fundAbs));
  const normalizeFlow = createNormalizer(prepared.map((market) => market.logTurnover));

  return prepared.map((preparedMarket) => {
    const liquidity =
      preparedMarket.logOiUsd === undefined
        ? NEUTRAL_SUBSCORE
        : normalizeLiquidity(preparedMarket.logOiUsd);

    const cost =
      preparedMarket.spreadBps === undefined
        ? NEUTRAL_SUBSCORE
        : 1 - normalizeSpread(preparedMarket.spreadBps);

    const carry =
      preparedMarket.fundAbs === undefined
        ? NEUTRAL_SUBSCORE
        : 1 - normalizeCarry(preparedMarket.fundAbs);

    const flow =
      preparedMarket.logTurnover === undefined
        ? NEUTRAL_SUBSCORE
        : Math.min(normalizeFlow(preparedMarket.logTurnover), FLOW_CAP) / FLOW_CAP;

    const baseScore =
      WEIGHTS.liquidity * liquidity +
      WEIGHTS.cost * cost +
      WEIGHTS.flow * flow +
      WEIGHTS.carry * carry;

    const activePenalty = preparedMarket.market.isActive
      ? ACTIVE_PENALTY.active
      : ACTIVE_PENALTY.inactive;

    return {
      ...preparedMarket.market,
      oiUsd: preparedMarket.oiUsd,
      turnover24h: preparedMarket.turnover,
      liquidityScore: liquidity,
      costScore: cost,
      flowScore: flow,
      carryScore: carry,
      marketScore: 100 * baseScore * activePenalty,
    };
  });
}
